import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { Search, MapPin, Home, ArrowLeft, X, Bed, Bath, Square, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';
import locationsData from '../data/locations.json';
import { PROPERTY_TYPES } from '../data/propertyTypes';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import { fixImageUrl } from '../lib/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 20;

const Properties = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // Filters collapsed by default on mobile, expanded on desktop
  const [filtersExpanded, setFiltersExpanded] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    district: '',
    village: '',
    propertyType: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    bathrooms: '',
    minSize: '',
    maxSize: '',
    status: 'available'
  });
  const [districtSearch, setDistrictSearch] = useState('');
  const [villageSearch, setVillageSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);
  const { user } = useAuth();

  // Get filtered districts based on search
  const getFilteredDistricts = () => {
    const search = districtSearch.toLowerCase();
    if (!search) return locationsData.districts;
    return locationsData.districts.filter(d => d.toLowerCase().includes(search));
  };

  // Get villages - search across all districts if no district selected
  const getFilteredVillages = () => {
    const search = villageSearch.toLowerCase();
    
    if (filters.district) {
      // If district is selected, only show villages from that district
      const villages = locationsData.villages[filters.district] || [];
      if (!search) return villages.slice(0, 20);
      return villages.filter(v => v.toLowerCase().includes(search)).slice(0, 20);
    } else {
      // No district selected - search across all villages
      if (!search) return [];
      const results = [];
      Object.entries(locationsData.villages).forEach(([district, villages]) => {
        villages.forEach(village => {
          if (village.toLowerCase().includes(search)) {
            results.push({ name: village, district: district });
          }
        });
      });
      return results.slice(0, 20);
    }
  };

  const handleDistrictSelect = (district) => {
    setFilters({ ...filters, district: district, village: '' });
    setDistrictSearch(district);
    setVillageSearch('');
    setShowDistrictDropdown(false);
  };

  const handleVillageSelect = (village, district = null) => {
    if (district && !filters.district) {
      // If selecting from all villages, also set the district
      setFilters({ ...filters, village: village, district: district });
      setDistrictSearch(district);
    } else {
      setFilters({ ...filters, village: village });
    }
    setVillageSearch(village);
    setShowVillageDropdown(false);
  };

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async (filterParams = filters, append = false, skipOverride = null) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      if (filterParams.district) params.append('district', filterParams.district);
      if (filterParams.village) params.append('city', filterParams.village);
      if (filterParams.propertyType) params.append('property_type', filterParams.propertyType);
      if (filterParams.minPrice) params.append('min_price', filterParams.minPrice);
      if (filterParams.maxPrice) params.append('max_price', filterParams.maxPrice);
      if (filterParams.bedrooms) params.append('bedrooms', filterParams.bedrooms);
      if (filterParams.bathrooms) params.append('bathrooms', filterParams.bathrooms);
      if (filterParams.minSize) params.append('min_size', filterParams.minSize);
      if (filterParams.maxSize) params.append('max_size', filterParams.maxSize);
      if (filterParams.status) params.append('status', filterParams.status);

      const skip = skipOverride ?? (append ? listings.length : 0);
      params.append('skip', String(skip));
      params.append('limit', String(PAGE_SIZE));

      const response = await axios.get(`${API}/listings/public?${params.toString()}`);
      const newBatch = response.data || [];
      setListings((prev) => (append ? [...prev, ...newBatch] : newBatch));
      setHasMore(newBatch.length === PAGE_SIZE);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchListings(filters, true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Use typed village/district text as filter if user didn't pick from dropdown
    const finalFilters = {
      ...filters,
      village: filters.village || villageSearch || '',
      district: filters.district || districtSearch || '',
    };
    setFilters(finalFilters);
    fetchListings(finalFilters);
  };

  const clearFilters = () => {
    const clearedFilters = { 
      district: '',
      village: '',
      propertyType: '',
      minPrice: '', 
      maxPrice: '', 
      bedrooms: '',
      bathrooms: '',
      minSize: '',
      maxSize: '',
      status: 'available' 
    };
    setFilters(clearedFilters);
    setDistrictSearch('');
    setVillageSearch('');
    fetchListings(clearedFilters);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="font-body text-sm hidden sm:inline">Back</span>
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_property-mgmt-43/artifacts/8gx78s2p_Signe%20logo.jpg" alt="Signé" className="h-8 w-8 rounded-full object-cover" />
              <span className="font-heading text-lg font-bold tracking-tight text-brand-brown">Signé <span className="text-brand-gold">Properties</span></span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/employee'} data-testid="dashboard-link">
                <Button className="rounded-none font-heading font-bold uppercase tracking-wide text-xs bg-brand-brown text-white hover:bg-brand-brown/80">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="login-link">
                Staff Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Browse Properties
          </h1>
          <p className="font-body text-muted-foreground max-w-2xl">
            Discover our curated selection of premium properties. Use the filters below to find your perfect home.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-border sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Filter toggle header */}
          {(() => {
            const activeFilterCount = [
              filters.district, filters.village, filters.propertyType,
              filters.minPrice, filters.maxPrice, filters.bedrooms,
              filters.bathrooms, filters.minSize, filters.maxSize
            ].filter(Boolean).length;
            return (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setFiltersExpanded((v) => !v)}
                  className="flex items-center gap-2 font-heading text-xs uppercase tracking-widest font-bold text-brand-brown hover:text-brand-gold transition-colors"
                  data-testid="toggle-filters-btn"
                  aria-expanded={filtersExpanded}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-brand-gold text-brand-brown text-[10px] px-2 py-0.5 rounded-full font-bold" data-testid="active-filter-count">
                      {activeFilterCount}
                    </span>
                  )}
                  {filtersExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="font-heading text-xs uppercase tracking-widest text-muted-foreground hover:text-brand-brown transition-colors flex items-center gap-1"
                    data-testid="clear-filters-inline"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                )}
              </div>
            );
          })()}

          {/* Collapsible form */}
          <form
            onSubmit={handleSearch}
            className={`flex flex-wrap gap-4 items-end overflow-hidden transition-all duration-300 ${filtersExpanded ? 'max-h-[1200px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}
            aria-hidden={!filtersExpanded}
          >
            {/* District Dropdown */}
            <div className="w-[180px] relative">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                District
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold" />
                <Input
                  placeholder="All Districts"
                  value={districtSearch}
                  onChange={(e) => {
                    setDistrictSearch(e.target.value);
                    setShowDistrictDropdown(true);
                  }}
                  onFocus={() => setShowDistrictDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
                  className="rounded-none border-2 pl-10 py-5 font-body"
                  data-testid="filter-district"
                />
              </div>
              
              {/* District Dropdown */}
              {showDistrictDropdown && (
                <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-64 overflow-y-auto">
                  {districtSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ ...filters, district: '', village: '' });
                        setDistrictSearch('');
                        setVillageSearch('');
                        setShowDistrictDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-secondary/50 text-muted-foreground text-sm border-b border-border/50"
                    >
                      Clear selection
                    </button>
                  )}
                  {getFilteredDistricts().map((district) => (
                    <button
                      key={district}
                      type="button"
                      onClick={() => handleDistrictSelect(district)}
                      className={`w-full px-4 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0 ${filters.district === district ? 'bg-brand-gold/10' : ''}`}
                    >
                      <MapPin className="w-3 h-3 text-brand-gold" />
                      <span className="font-body text-sm">{district}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Village Dropdown */}
            <div className="w-[200px] relative">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Village
              </label>
              <div className="relative">
                <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search village..."
                  value={villageSearch}
                  onChange={(e) => {
                    setVillageSearch(e.target.value);
                    setShowVillageDropdown(true);
                  }}
                  onFocus={() => setShowVillageDropdown(true)}
                  onBlur={() => setTimeout(() => setShowVillageDropdown(false), 200)}
                  className="rounded-none border-2 pl-10 py-5 font-body"
                  data-testid="filter-village"
                />
              </div>
              
              {/* Village Dropdown */}
              {showVillageDropdown && villageSearch && (
                <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-64 overflow-y-auto">
                  {villageSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ ...filters, village: '' });
                        setVillageSearch('');
                        setShowVillageDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-secondary/50 text-muted-foreground text-sm border-b border-border/50"
                    >
                      Clear selection
                    </button>
                  )}
                  {getFilteredVillages().length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground">
                      {villageSearch.length < 2 ? 'Type at least 2 characters...' : 'No villages found'}
                    </div>
                  ) : (
                    getFilteredVillages().map((item, idx) => {
                      const villageName = typeof item === 'string' ? item : item.name;
                      const districtName = typeof item === 'string' ? filters.district : item.district;
                      return (
                        <button
                          key={`${villageName}-${idx}`}
                          type="button"
                          onClick={() => handleVillageSelect(villageName, districtName)}
                          className={`w-full px-4 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0 ${filters.village === villageName ? 'bg-brand-gold/10' : ''}`}
                        >
                          <Home className="w-3 h-3 text-muted-foreground" />
                          <span className="font-body text-sm">{villageName}</span>
                          {!filters.district && districtName && (
                            <span className="font-body text-xs text-muted-foreground ml-auto">
                              {districtName}
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Price Filters */}
            <div className="w-[120px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Min Price
              </label>
              <Input
                type="number"
                placeholder="$0"
                value={filters.minPrice}
                onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                className="rounded-none border-2 py-5 font-body"
                data-testid="filter-min-price"
              />
            </div>
            <div className="w-[120px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Max Price
              </label>
              <Input
                type="number"
                placeholder="No max"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="rounded-none border-2 py-5 font-body"
                data-testid="filter-max-price"
              />
            </div>

            {/* Property Type */}
            <div className="w-[160px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Type
              </label>
              <Select value={filters.propertyType || 'any'} onValueChange={(value) => setFilters({ ...filters, propertyType: value === 'any' ? '' : value })}>
                <SelectTrigger className="rounded-none border-2 py-5 font-body" data-testid="filter-property-type">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {PROPERTY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bedrooms & Bathrooms */}
            <div className="w-[100px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Beds
              </label>
              <Select value={filters.bedrooms} onValueChange={(value) => setFilters({ ...filters, bedrooms: value === 'any' ? '' : value })}>
                <SelectTrigger className="rounded-none border-2 py-5 font-body" data-testid="filter-bedrooms">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1+</SelectItem>
                  <SelectItem value="2">2+</SelectItem>
                  <SelectItem value="3">3+</SelectItem>
                  <SelectItem value="4">4+</SelectItem>
                  <SelectItem value="5">5+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[100px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Baths
              </label>
              <Select value={filters.bathrooms} onValueChange={(value) => setFilters({ ...filters, bathrooms: value === 'any' ? '' : value })}>
                <SelectTrigger className="rounded-none border-2 py-5 font-body" data-testid="filter-bathrooms">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="1">1+</SelectItem>
                  <SelectItem value="2">2+</SelectItem>
                  <SelectItem value="3">3+</SelectItem>
                  <SelectItem value="4">4+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Size Filters */}
            <div className="w-[110px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Min m²
              </label>
              <Input
                type="number"
                placeholder="0"
                value={filters.minSize}
                onChange={(e) => setFilters({ ...filters, minSize: e.target.value })}
                className="rounded-none border-2 py-5 font-body"
                data-testid="filter-min-size"
              />
            </div>
            <div className="w-[110px]">
              <label className="font-heading text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
                Max m²
              </label>
              <Input
                type="number"
                placeholder="No max"
                value={filters.maxSize}
                onChange={(e) => setFilters({ ...filters, maxSize: e.target.value })}
                className="rounded-none border-2 py-5 font-body"
                data-testid="filter-max-size"
              />
            </div>

            <Button
              type="submit"
              className="rounded-none font-heading font-bold uppercase tracking-wide text-xs bg-brand-gold text-brand-brown hover:bg-brand-gold/90 py-5"
              data-testid="apply-filters"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white shadow-sm overflow-hidden animate-pulse">
                <div className="h-64 bg-muted"></div>
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-muted w-1/2"></div>
                  <div className="h-6 bg-muted w-3/4"></div>
                  <div className="h-5 bg-muted w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : listings.length > 0 ? (
          <>
            <p className="font-body text-muted-foreground mb-6">
              Showing {listings.length} {listings.length === 1 ? 'property' : 'properties'}{hasMore ? '  ·  more available' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing, index) => (
                <Link 
                  key={listing.id} 
                  to={`/property/${listing.id}`}
                  className="group block"
                  data-testid={`property-card-${index}`}
                >
                  <div className="bg-white shadow-sm overflow-hidden hover-lift">
                    <div className="relative h-64 overflow-hidden">
                      {listing.images && listing.images.length > 0 ? (
                        <img 
                          src={fixImageUrl(listing.images[0])} 
                          alt={listing.title}
                          className="w-full h-full object-cover listing-image"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <Home className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <Badge 
                        className={`absolute top-4 left-4 rounded-none px-3 py-1 font-heading text-[10px] uppercase tracking-widest font-bold ${
                          listing.status === 'sold' 
                            ? 'bg-brand-gold text-brand-brown' 
                            : listing.status === 'pending'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-brand-brown text-white'
                        }`}
                      >
                        {listing.status}
                      </Badge>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span className="font-body text-sm">{listing.city || listing.address}</span>
                        {(listing.property_type || listing.custom_fields?.property_type) && (
                          <>
                            <span className="text-muted-foreground/50">·</span>
                            <span className="font-heading text-[10px] uppercase tracking-widest font-bold text-brand-gold" data-testid={`card-property-type-${listing.id}`}>
                              {listing.property_type || listing.custom_fields?.property_type}
                            </span>
                          </>
                        )}
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-foreground group-hover:text-brand-gold transition-colors line-clamp-1">
                        {listing.title}
                      </h3>
                      {/* Property Details */}
                      {(listing.bedrooms || listing.bathrooms || listing.sqft) && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {listing.bedrooms && (
                            <span className="flex items-center gap-1">
                              <Bed className="w-4 h-4" />
                              {listing.bedrooms} {listing.bedrooms === 1 ? 'Bed' : 'Beds'}
                            </span>
                          )}
                          {listing.bathrooms && (
                            <span className="flex items-center gap-1">
                              <Bath className="w-4 h-4" />
                              {listing.bathrooms} {listing.bathrooms === 1 ? 'Bath' : 'Baths'}
                            </span>
                          )}
                          {listing.sqft && (
                            <span className="flex items-center gap-1">
                              <Square className="w-4 h-4" />
                              {listing.sqft.toLocaleString()} m²
                            </span>
                          )}
                        </div>
                      )}
                      <p className="font-heading text-xl font-bold text-brand-gold">
                        {formatPrice(listing.price)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center pt-12">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-brown text-white hover:bg-brand-brown/90 px-10 py-6"
                  data-testid="load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>Load More Properties</>
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No properties found</h3>
            <p className="font-body text-muted-foreground mb-6">
              Try adjusting your filters or check back later for new listings.
            </p>
            <Button
              onClick={clearFilters}
              variant="outline"
              className="rounded-none border-2 border-brand-brown font-heading uppercase tracking-wide"
              data-testid="clear-all-filters"
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp />
    </div>
  );
};

export default Properties;
